/* iceberg UI */
import { Flex, IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Add } from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    Tab,
    TableCell,
    TextField,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { SimpleGoogleLink } from 'application/Shared/Components/Buttons';
import { DateFormat } from 'application/Shared/Components/Core';
import FileUpload from 'application/Shared/Components/FileUpload';
import { MultiInput } from 'application/Shared/Components/Inputs';
import { MUITabsWrapper } from 'application/Shared/Components/MUITabsWrapper';
import {
    CategoryPicker,
    PublisherPicker
} from 'application/Shared/Components/Pickers';
import { AuvikSwitch } from 'application/Shared/Components/Switches';
import { ManagedTable } from 'application/Shared/Components/Table';
import { FontSizes } from 'application/Shared/Constants';
import { Notify, Payload, Transition } from 'application/Shared/Helpers';
import { Checkmark } from 'application/Shared/Icons';
import { Confirm } from 'application/Shared/Modals';
import { debug } from 'application/Shared/Utils';

/* Hooks */
import {
    useDeleteApplication,
    useFetchApplicationByParams,
    usePutApplication
} from 'hooks/dataAccess/Application/application';
import { usePostApplicationMerge } from 'hooks/dataAccess/Application/applicationMerge';
import {
    useDeleteUsernameRefBulk,
    usePostUsernameRefBulk,
    usePutUsernameRefBulk
} from 'hooks/dataAccess/UsernameRef/usernameRefBulk';

/*  images  */
import { ReactComponent as GoogleAdminLogo } from 'images/Google_Admin.svg';

/*  Objects  */
import {
    Application,
    ApplicationIcon,
    ApplicationImage
} from 'objects/Application';

/*  React  */
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import ReactJson from 'react-json-view';

/*  Constants  */
const usernameRefTypes = ['id', 'class'];

const copy = ({ id, t }) => {
    const copyText = document.getElementById(id);

    /* Select the text field */
    copyText.select();
    copyText.setSelectionRange(0, 99999); /* For mobile devices */

    /* Copy the text inside the text field */
    navigator.clipboard.writeText(copyText.value);
    Notify.s(t('Common.copied'));
};

const updateRefs = async ({ params, putUsernameRefBulk }) => {
    const { data } = params;

    const payload = [];
    data.forEach((note) => {
        const { id, type, ref } = note;
        if (!ref) {
            return;
        }
        payload.push({ id, ref, type });
    });

    debug.dev('Updating username refs', payload);
    await putUsernameRefBulk({ params: payload });
};

const deleteRefs = async ({ params, deleteUsernameRefBulk }) => {
    const { data } = params;

    const ids = [];
    data.forEach((n) => {
        ids.push(n.id);
    });

    debug.dev('Deleting username refs', ids);

    await deleteUsernameRefBulk({ params: { id: Payload.idString(ids) } });
};

const Tenant = ({ row }) => {
    const { tenant, classified, dateCreated } = row;

    return (
        <>
            <TableCell>
                {tenant.name}
                <br />
                <small>{tenant.id}</small>
            </TableCell>
            <TableCell>
                <DateFormat date={dateCreated} />
            </TableCell>
            <TableCell align="center">
                <Checkmark checked={classified} />
            </TableCell>
        </>
    );
};

const addRefs = async ({ application, data, postUsernameRefBulk }) => {
    const applicationId = application.id;
    const payload = [];
    data.forEach((note) => {
        const { type, ref } = note;
        if (!ref) {
            return;
        }
        payload.push({ applicationId, ref, type });
    });

    debug.dev('Adding username refs', data);
    await postUsernameRefBulk({ params: payload });
};

const handleSaveRefs = async ({
    application,
    deleteUsernameRefBulk,
    postUsernameRefBulk,
    putUsernameRefBulk,
    setCurrentApplication,
    usernameRefsAdded,
    usernameRefsAll,
    usernameRefsModified,
    usernameRefsRemoved
}) => {
    const added = usernameRefsAdded.filter((d) => d.ref);
    if (added.length) {
        await addRefs({ application, data: added, postUsernameRefBulk });
    }

    const modified = usernameRefsModified.filter((d) => d.ref);
    if (modified.length) {
        await updateRefs({ data: modified, putUsernameRefBulk });
    }

    if (usernameRefsRemoved.length) {
        await deleteRefs({ data: usernameRefsRemoved, deleteUsernameRefBulk });
    }

    setCurrentApplication({
        ...application,
        usernameRefs: usernameRefsAll
    });
};

const handleClose = ({ onClose, rowMap }) => {
    if (onClose) {
        onClose(rowMap);
    }
};

const save = async ({
    application,
    categoryMap,
    deleteUsernameRefBulk,
    onClose,
    postUsernameRefBulk,
    putApplication,
    putUsernameRefBulk,
    rowMap,
    setCurrentApplication,
    usernameRefsAdded,
    usernameRefsAll,
    usernameRefsModified,
    usernameRefsRemoved
}) => {
    const { id, categoryId } = application;
    await handleSaveRefs({
        application,
        deleteUsernameRefBulk,
        postUsernameRefBulk,
        putUsernameRefBulk,
        setCurrentApplication,
        usernameRefsAdded,
        usernameRefsAll,
        usernameRefsModified,
        usernameRefsRemoved
    });

    let baseName = '';
    try {
        baseName = new URL(application.baseName).hostname;
    } catch (err) {
        baseName = application.baseName;
    }

    const appName = application?.name?.trim() || baseName;

    let payload = {
        azureSignIn: application.azureSignIn,
        baseName,
        description: application.description,
        disabled: application.disabled,
        forceSynchronousPut: true,
        googleSignIn: application.googleSignIn,
        googleSSO: application.googleSSO,
        iconUpload: application.iconUpload,
        identifiers: application.identifiers.map((i) => i.identifier),
        imageUpload: application.imageUpload,
        microsoftSSO: application.microsoftSSO,
        monitored: application.monitored,
        name: appName,
        publisherId: application.publisherId,
        workatoProvider: application.workatoProvider
    };

    if (payload.identifiers.indexOf(payload.baseName) === -1) {
        payload.identifiers.push(payload.baseName);
    }
    if (categoryId !== null) {
        payload = { ...payload, categoryId };
    }

    const success = await putApplication({
        applicationId: application.id,
        params: payload
    });

    if (success) {
        // Update Table Row
        let row = application;
        if (categoryMap[categoryId]) {
            const category = categoryMap[categoryId];
            row = {
                ...application,
                category: category.category,
                categoryId,
                subCategory: category.subCategory
            };
        }

        handleClose({ onClose, rowMap: { ...rowMap, [id]: row } });
    }
};

const merge = async ({
    application,
    onClose,
    postApplicationMerge,
    row,
    rowMap
}) => {
    if (row.id !== application.id) {
        const success = await postApplicationMerge({
            fromId: application.id,
            toId: row.id
        });

        if (success) {
            const currentRowMap = { ...rowMap };
            // Remove Table Row
            delete currentRowMap[application.id];
            handleClose({ onClose, rowMap: currentRowMap });
        }
    }
};

const MergeRow = ({
    application,
    onClose,
    postApplicationMerge,
    row,
    rowMap
}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    return (
        <Application data={row}>
            <TableCell align="right">
                {application.id !== row.id && (
                    <Box
                        onClick={() => setOpen(true)}
                        sx={{ cursor: 'pointer' }}
                    >
                        <Add sx={{ color: 'button.primary' }} />
                    </Box>
                )}
            </TableCell>
            <TableCell>
                <ApplicationNameLinkCell withCategory nameClassname="bold" />
                <small>{row.baseName}</small>
            </TableCell>
            {open && (
                <Confirm
                    key={row.id}
                    modalOpen={!!open}
                    promptCancelFn={() => setOpen(false)}
                    promptTitle={t(
                        'GlobalAdmin.Applications.merge.confirmation.title'
                    )}
                    promptAlertText={t(
                        'GlobalAdmin.Applications.merge.confirmation.alertText'
                    )}
                    promptAlertTextType="error"
                    promptAlertIcon
                    promptText={
                        <Box width="500px">
                            <Typography variant="p">
                                <Trans
                                    i18nKey="GlobalAdmin.Applications.merge.confirmation.description"
                                    values={{
                                        fromApp: application.name,
                                        toApp: row.name
                                    }}
                                    components={{
                                        bold: (
                                            <span style={{ fontWeight: 600 }} />
                                        ),
                                        br: <br />
                                    }}
                                />
                            </Typography>
                        </Box>
                    }
                    promptConfirmFn={async () => {
                        await merge({
                            application,
                            onClose,
                            postApplicationMerge,
                            row,
                            rowMap
                        });
                        setOpen(false);
                    }}
                />
            )}
        </Application>
    );
};

const deleteFn = async ({
    application,
    deleteApplication,
    onClose,
    rowMap
}) => {
    const { id } = application;
    const success = await deleteApplication({ applicationId: application.id });

    if (success) {
        const currentRowMap = { ...rowMap };
        // Remove Table Row
        delete currentRowMap[id];
        handleClose({ onClose, rowMap: currentRowMap });
    }
};

const Metadata = ({ application }) => {
    const theme = useTheme();
    const { t } = useTranslation();

    if (!application.metadata) {
        return null;
    }

    const { metadata } = application;

    return (
        <Grid
            container
            spacing={3}
            data-trackid="globalAdminAppModalRenderMetadata"
        >
            <Grid item xs={12}>
                <Typography variant="h4">{t('Common.metadata')}</Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography style={{ fontWeight: 600 }}>
                    {`${t('Common.file_description')}: `}
                </Typography>
                <input
                    type="text"
                    value={metadata.FileDescription}
                    id="fdesc"
                    style={{ cursor: 'pointer' }}
                    onClick={() => copy({ id: 'fdesc', t })}
                />
                <SimpleGoogleLink searchField={metadata.FileDescription} />
            </Grid>
            <Grid item xs={12}>
                <Typography style={{ fontWeight: 600 }}>
                    {`${t('Common.internal_name')}: `}
                </Typography>
                <input
                    type="text"
                    value={metadata.InternalName}
                    id="iname"
                    style={{ cursor: 'pointer' }}
                    onClick={() => copy({ id: 'iname', t })}
                />
                <SimpleGoogleLink searchField={metadata.InternalName} />
            </Grid>
            <Grid item xs={12}>
                <Typography style={{ fontWeight: 600 }}>
                    {`${t('Common.product_name')}: `}
                </Typography>
                <input
                    type="text"
                    value={metadata.ProductName}
                    id="pname"
                    style={{ cursor: 'pointer' }}
                    onClick={() => copy({ id: 'pname', t })}
                />
                <SimpleGoogleLink searchField={metadata.ProductName} />
            </Grid>
            <Grid item xs={12}>
                <Typography style={{ fontWeight: 600 }}>
                    {`${t('Common.company_name')}: `}
                </Typography>
                <input
                    type="text"
                    value={metadata.CompanyName}
                    id="cname"
                    style={{ cursor: 'pointer' }}
                    onClick={() => copy({ id: 'cname', t })}
                />
                <SimpleGoogleLink searchField={metadata.CompanyName} />
            </Grid>
            <hr />
            <Grid item xs={12}>
                <ReactJson
                    style={{
                        backgroundColor: theme.palette.metadata.background,
                        width: '100%'
                    }}
                    name="Metadata"
                    theme="monokai"
                    collapseStringsAfterLength={140}
                    src={metadata}
                    enableClipboard={false}
                />
            </Grid>
        </Grid>
    );
};

const CategoryButtons = ({
    application,
    personalFinance,
    personalId,
    personalShopping,
    setButtonClicked,
    setCurrentApplication,
    unknownId
}) => {
    const { t } = useTranslation();
    return (
        <>
            <Button
                onClick={() => {
                    setCurrentApplication({
                        ...application,
                        categoryId: personalId
                    });
                    setButtonClicked(true);
                }}
                color="primary"
                variant="outlined"
                style={{ marginRight: '4px' }}
            >
                {t('GlobalAdmin.Applications.p_other')}
            </Button>
            <Button
                onClick={() => {
                    setCurrentApplication({
                        ...application,
                        categoryId: personalFinance
                    });
                    setButtonClicked(true);
                }}
                color="primary"
                variant="outlined"
                style={{ marginRight: '4px' }}
            >
                {t('GlobalAdmin.Applications.p_finance')}
            </Button>
            <Button
                onClick={() => {
                    setCurrentApplication({
                        ...application,
                        categoryId: personalShopping
                    });
                    setButtonClicked(true);
                }}
                color="primary"
                variant="outlined"
                style={{ marginRight: '4px' }}
            >
                {t('GlobalAdmin.Applications.p_shopping')}
            </Button>
            <Button
                onClick={() => {
                    setCurrentApplication({
                        ...application,
                        categoryId: unknownId
                    });
                    setButtonClicked(true);
                }}
                color="primary"
                variant="outlined"
            >
                {t('Common.unknown')}
            </Button>
        </>
    );
};

const DisplayUsernameRefs = ({
    application,
    setUsernameRefsAdded,
    setUsernameRefsAll,
    setUsernameRefsModified,
    setUsernameRefsRemoved
}) => {
    const { t } = useTranslation();
    const { usernameRefs } = application;

    return (
        <>
            <Typography style={{ fontWeight: 600 }}>
                {t('GlobalAdmin.Applications.usernameRefs')}
            </Typography>
            <MultiInput
                fields={[
                    {
                        default: usernameRefTypes[0],
                        field: 'type',
                        items: usernameRefTypes,
                        label: t('GlobalAdmin.Applications.type'),
                        type: 'select'
                    },
                    {
                        field: 'ref',
                        label: t('GlobalAdmin.Applications.ref')
                    }
                ]}
                key="usernameRefs"
                max={5}
                min={0}
                onChange={(p) => {
                    const { added, modified, removed, rows } = p;
                    debug.dev(
                        'ADDED / MODIFIED / REMOVED / ROWS',
                        added,
                        modified,
                        removed,
                        rows
                    );
                    setUsernameRefsAdded(Object.values(added));
                    setUsernameRefsModified(Object.values(modified));
                    setUsernameRefsRemoved(Object.values(removed));
                    setUsernameRefsAll(rows);
                }}
                values={usernameRefs || []}
                zeroText={t('GlobalAdmin.Applications.add_username_refs')}
            />
        </>
    );
};

const AppDetails = ({
    application,
    buttonClicked,
    categories,
    personalFinance,
    personalId,
    personalShopping,
    setButtonClicked,
    setCurrentApplication,
    setUsernameRefsAdded,
    setUsernameRefsAll,
    setUsernameRefsModified,
    setUsernameRefsRemoved,
    unknownId
}) => {
    const { t } = useTranslation();
    const { baseName, categoryId } = application;

    // SSO options
    // fieldName = column name
    const ssoConfig = {
        googleSignIn: {
            fieldName: 'googleSSO',
            icon: (
                <IconWithVariant
                    variant="avatar"
                    src="Google"
                    size={FontSizes[1]}
                />
            ),
            label: t('Common.google')
        },
        googleWorkspace: {
            fieldName: 'googleSignIn',
            icon: (
                <Box
                    alt="Google Logo"
                    sx={{
                        color: 'icon.avatar',
                        width: '14px'
                    }}
                >
                    <GoogleAdminLogo />
                </Box>
            ),
            label: t('Common.google_workspace')
        },
        microsoftSSO: {
            fieldName: 'microsoftSSO',
            icon: (
                <IconWithVariant
                    variant="avatar"
                    src="Microsoft"
                    size={FontSizes[1]}
                />
            ),
            label: t('Common.microsoft')
        },
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        microsoftEntra: {
            fieldName: 'azureSignIn',
            icon: (
                <IconWithVariant
                    variant="avatar"
                    src="Windows"
                    size={FontSizes[1]}
                />
            ),
            label: t('Common.microsoftEntraID')
        }
    };

    return (
        <Grid
            container
            spacing={3}
            data-trackid="globalAdminAppModalRenderAppDetails"
        >
            <Grid item xs={12}>
                <Typography variant="h4">{t('Common.application')}</Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography>
                    <a
                        className="link underline"
                        href={`https://${baseName}`}
                        target="external"
                        rel="noopener noreferrer nofollow"
                    >
                        {t('GlobalAdmin.Applications.open_url')}
                    </a>
                    &nbsp;/&nbsp;
                    <a
                        className="link underline"
                        href={`https://www.google.com/search?q=${baseName}`}
                        target="external"
                        rel="noopener noreferrer nofollow"
                    >
                        {t('GlobalAdmin.Applications.open_google_search')}
                    </a>
                </Typography>
                <hr />
                <Typography style={{ fontWeight: 600 }}>
                    {t('Common.name')}
                </Typography>
                <TextField
                    fullWidth
                    onChange={(e) =>
                        setCurrentApplication({
                            ...application,
                            name: e.target.value
                        })
                    }
                    value={application.name || ''}
                />
            </Grid>
            <Grid item xs={12}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={application.disabled}
                            color="primary"
                            onChange={() =>
                                setCurrentApplication({
                                    ...application,
                                    disabled: !application.disabled
                                })
                            }
                            required
                        />
                    }
                    label={t('GlobalAdmin.Applications.disable_app')}
                />
            </Grid>
            <Grid item xs={12}>
                <Typography style={{ fontWeight: 600 }}>
                    {t('GlobalAdmin.Applications.global_usage', {
                        usage: application.usage
                    })}
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography style={{ fontWeight: 600 }}>
                    {t('GlobalAdmin.Applications.icon')}
                </Typography>
                <FileUpload
                    width="30px"
                    height="30px"
                    callbackFn={(base64Image) => {
                        setCurrentApplication({
                            ...application,
                            iconUpload: base64Image
                        });
                    }}
                >
                    <ApplicationIcon sx={{ height: '16px', width: '16px' }} />
                </FileUpload>
            </Grid>
            <Grid item xs={12}>
                <Typography style={{ fontWeight: 600 }}>
                    {t('Common.category')}
                </Typography>
                {buttonClicked ? (
                    <CategoryPicker
                        data={categories}
                        key="staticValue"
                        multiple={false}
                        disableAllOption
                        unselectedOption="No Category Selected"
                        unselectedValue={null}
                        value={categoryId}
                        reloadValue={categoryId}
                        onChange={(e) => {
                            setCurrentApplication({
                                ...application,
                                categoryId: e
                            });
                            setButtonClicked(false);
                        }}
                    />
                ) : (
                    <CategoryPicker
                        data={categories}
                        key="nonStaticValue"
                        multiple={false}
                        disableAllOption
                        unselectedOption="No Category Selected"
                        unselectedValue={null}
                        value={categoryId}
                        reloadValue={categoryId}
                        onChange={(e) =>
                            setCurrentApplication({
                                ...application,
                                categoryId: e
                            })
                        }
                    />
                )}
            </Grid>
            <Grid item xs={12} zeroMinWidth>
                <CategoryButtons
                    application={application}
                    personalFinance={personalFinance}
                    personalId={personalId}
                    personalShopping={personalShopping}
                    setButtonClicked={setButtonClicked}
                    setCurrentApplication={setCurrentApplication}
                    unknownId={unknownId}
                />
            </Grid>
            <Grid item xs={12} zeroMinWidth>
                <Typography style={{ fontWeight: 600 }}>
                    {t('Common.publisher')}
                </Typography>
                <PublisherPicker
                    allowAdd
                    allValue={t('GlobalAdmin.Publisher.choose')}
                    publisherId={application.publisherId}
                    onChange={(value) => {
                        setCurrentApplication({
                            ...application,
                            publisherId: value
                        });
                    }}
                />
            </Grid>
            <Grid item xs={12} zeroMinWidth>
                <Typography style={{ fontWeight: 600 }}>
                    {t('GlobalAdmin.Application.workatoProvider')}
                </Typography>
                <TextField
                    fullWidth
                    onChange={(e) =>
                        setCurrentApplication({
                            ...application,
                            workatoProvider: e.target.value
                        })
                    }
                    value={application.workatoProvider || ''}
                />
            </Grid>
            <Grid item xs={12}>
                <Typography style={{ fontWeight: 600 }}>
                    {t('GlobalAdmin.Applications.base_name')}
                </Typography>
                <TextField
                    fullWidth
                    onChange={(e) =>
                        setCurrentApplication({
                            ...application,
                            baseName: e.target.value
                        })
                    }
                    value={baseName}
                />
            </Grid>
            <Grid item xs={12}>
                <Typography style={{ fontWeight: 600 }}>
                    {t('GlobalAdmin.Applications.identifiers')}
                </Typography>
                <MultiInput
                    fields={[
                        {
                            field: 'identifier',
                            label: t('GlobalAdmin.Applications.identifier')
                        },
                        {
                            disabled: true,
                            field: 'identifierRegex',
                            label: t('GlobalAdmin.Applications.identifierRegex')
                        }
                    ]}
                    key="identifiers"
                    max={5}
                    min={0}
                    onChange={(p) => {
                        const identifiers = Array.from(
                            new Set(
                                p.rows
                                    .filter((row) => !!row.identifier)
                                    .map((a) => ({
                                        id: a.id,
                                        identifier: a.identifier
                                            .trim()
                                            .toLowerCase(),
                                        identifierRegex: a.identifierRegex
                                    }))
                            )
                        );
                        setCurrentApplication({
                            ...application,
                            identifiers
                        });
                    }}
                    values={
                        application.identifiers &&
                        application.identifiers.map((a) => ({
                            identifier: a.identifier,
                            identifierRegex: a.identifierRegex
                        }))
                    }
                    zeroText={t('GlobalAdmin.Applications.add_identifiers')}
                />
            </Grid>
            <Grid item xs={12}>
                <DisplayUsernameRefs
                    application={application}
                    setUsernameRefsAdded={setUsernameRefsAdded}
                    setUsernameRefsAll={setUsernameRefsAll}
                    setUsernameRefsModified={setUsernameRefsModified}
                    setUsernameRefsRemoved={setUsernameRefsRemoved}
                />
            </Grid>
            <Grid item xs={12}>
                <Typography style={{ fontWeight: 600 }}>
                    {t('Common.description')}
                </Typography>
                <TextField
                    multiline
                    onChange={(e) =>
                        setCurrentApplication({
                            ...application,
                            description: e.target.value
                        })
                    }
                    rows={4}
                    style={{ width: '100%' }}
                    value={application.description || ''}
                    variant="outlined"
                />
            </Grid>
            <Grid item xs={12}>
                <Typography style={{ fontWeight: 600 }}>
                    {t('Common.image')}
                </Typography>
                <FileUpload
                    width="100%"
                    height="100px"
                    callbackFn={(base64Image) => {
                        setCurrentApplication({
                            ...application,
                            imageUpload: base64Image
                        });
                    }}
                >
                    <ApplicationImage
                        showTextForNoImage
                        style={{ height: 100, width: '100%' }}
                        displayEmpty
                    />
                </FileUpload>
            </Grid>
            <Grid item xs={12}>
                <Typography style={{ fontWeight: 600 }}>
                    {t('Common.sso')}
                </Typography>

                {Object.keys(ssoConfig).map((key) => (
                    <Box key={`ssoRadioButtons_${key}`} width="100%">
                        <FormControl
                            style={{
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                marginBottom: '8px'
                            }}
                        >
                            <Box width="40%">
                                <FormLabel
                                    id={`radiolabel_${key}`}
                                    style={{
                                        alignItems: 'center',
                                        display: 'flex'
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            backgroundColor:
                                                'background.avatar',
                                            marginRight: '10px'
                                        }}
                                    >
                                        {ssoConfig[key].icon}
                                    </Avatar>
                                    {ssoConfig[key].label}
                                </FormLabel>
                            </Box>
                            <Box
                                width="60%"
                                display="flex"
                                justifyContent="flex-end"
                            >
                                <RadioGroup
                                    row
                                    aria-labelledby={`globaladmin-radiogroup-${key}`}
                                    name="globaladmin-radio-buttons-group"
                                    value={
                                        application[ssoConfig[key].fieldName]
                                            ? 'detected'
                                            : 'notDetected'
                                    }
                                    onChange={(e) => {
                                        setCurrentApplication({
                                            ...application,
                                            [ssoConfig[key].fieldName]:
                                                e.target.value ===
                                                t('Common.detected')
                                        });
                                    }}
                                >
                                    <FormControlLabel
                                        value="detected"
                                        control={<Radio />}
                                        label={t('Common.detected')}
                                    />
                                    <FormControlLabel
                                        value="notDetected"
                                        control={<Radio />}
                                        label={t('Common.not_detected')}
                                    />
                                </RadioGroup>
                            </Box>
                        </FormControl>
                    </Box>
                ))}
            </Grid>
            <Grid item xs={12}>
                <FormControl
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        marginBottom: '8px',
                        width: '30%'
                    }}
                >
                    <FormControlLabel
                        control={
                            <Box>
                                <AuvikSwitch
                                    checked={application.monitored}
                                    onChange={(e) => {
                                        setCurrentApplication({
                                            ...application,
                                            monitored: e.target.checked
                                        });
                                    }}
                                    inputProps={{
                                        'aria-label': 'controlled'
                                    }}
                                />
                            </Box>
                        }
                        label={
                            <Typography
                                variant="p"
                                style={{
                                    fontWeight: 600,
                                    marginLeft: '8px'
                                }}
                            >
                                {application.monitored
                                    ? t('Common.monitored')
                                    : t('Common.notMonitored')}
                            </Typography>
                        }
                        style={{ marginLeft: 0 }}
                    />
                </FormControl>
            </Grid>
        </Grid>
    );
};

const headersApplication = (t) => [
    {
        align: 'right',
        field: null,
        renderHeader: <strong>{t('Common.actions')}</strong>,
        width: '5%'
    },
    {
        align: 'left',
        field: 'name',
        renderHeader: <strong>{t('Common.application')}</strong>,
        width: '95%'
    }
];

const searchFieldsApplication = [
    'baseName',
    'category',
    'description',
    'id',
    'name'
];

const GlobalApplication = ({
    application,
    buttonClicked,
    categories,
    categorized,
    onClose,
    personalFinance,
    personalId,
    personalShopping,
    postApplicationMerge,
    rowMap,
    setButtonClicked,
    setCurrentApplication,
    setUsernameRefsAdded,
    setUsernameRefsAll,
    setUsernameRefsModified,
    setUsernameRefsRemoved,
    unknownId
}) => {
    const { t } = useTranslation();

    const hasMetadata = application.metadata;

    const defaultSearch = application.name
        ? application.name.split(' ')[0]
        : '';

    const tableHeadersApplication = useMemo(() => headersApplication(t), [t]);

    return (
        <Grid container spacing={3}>
            <Grid
                item
                xs={hasMetadata ? 4 : 6}
                style={{ borderRight: 'solid', padding: '16px' }}
                zeroMinWidth
            >
                <Typography variant="h4">
                    {t('GlobalAdmin.Applications.merge_app_into', {
                        appName: application.name
                    })}
                </Typography>
                <Box data-trackid="globalAdminAppModalApplicationTab">
                    <ManagedTable
                        defaultSearch={defaultSearch}
                        headers={tableHeadersApplication}
                        renderBody={(row) => (
                            <MergeRow
                                application={application}
                                onClose={onClose}
                                postApplicationMerge={postApplicationMerge}
                                row={row}
                                rowMap={rowMap}
                            />
                        )}
                        renderBodyKey="id"
                        renderFooter
                        rows={categorized}
                        rowsPerPage={100}
                        searchEnabled
                        searchFields={searchFieldsApplication}
                        sortDirection="ASC"
                        sortFieldType="string"
                        sortKey="name"
                    />
                </Box>
            </Grid>
            <Grid
                item
                xs={hasMetadata ? 4 : 6}
                style={{
                    borderRight: hasMetadata ? 'solid' : 'none',
                    padding: '16px'
                }}
            >
                <AppDetails
                    application={application}
                    buttonClicked={buttonClicked}
                    categories={categories}
                    personalFinance={personalFinance}
                    personalId={personalId}
                    personalShopping={personalShopping}
                    setButtonClicked={setButtonClicked}
                    setCurrentApplication={setCurrentApplication}
                    setUsernameRefsAdded={setUsernameRefsAdded}
                    setUsernameRefsAll={setUsernameRefsAll}
                    setUsernameRefsModified={setUsernameRefsModified}
                    setUsernameRefsRemoved={setUsernameRefsRemoved}
                    unknownId={unknownId}
                />
            </Grid>
            {hasMetadata && (
                <Grid
                    item
                    xs={4}
                    style={{
                        padding: '16px'
                    }}
                >
                    <Metadata application={application} />
                </Grid>
            )}
        </Grid>
    );
};

const headersTenants = (t) => [
    {
        align: 'left',
        field: 'name',
        renderHeader: <strong>{t('Common.tenant')}</strong>,
        width: '50%'
    },
    {
        align: 'left',
        field: 'dateCreated',
        renderHeader: (
            <strong>{t('GlobalAdmin.Applications.date_app_found')}</strong>
        ),
        width: '45%'
    },
    {
        align: 'center',
        field: 'classified',
        renderHeader: <strong>{t('Common.classified')}</strong>,
        width: '5%'
    }
];

const searchFieldsTenants = ['description', 'category', 'baseName'];

const Tenants = ({ application }) => {
    const { t } = useTranslation();
    const applicationId = application.id;
    const parameters = useMemo(
        () => ({
            applicationId,
            global: true,
            includeTenant: true
        }),
        [applicationId]
    );
    const tableHeadersTenants = useMemo(() => headersTenants(t), [t]);

    return (
        <ManagedTable
            headers={tableHeadersTenants}
            parameters={parameters}
            renderBody={(row) => <Tenant row={row} />}
            renderBodyKey="id"
            renderFooter
            rowHeight={33}
            rowsApi="/tenantApp"
            rowsApiPreload
            rowsPerPage={10}
            searchEnabled={false}
            searchFields={searchFieldsTenants}
            sortDirection="ASC"
            sortFieldType="string"
            sortKey="name"
        />
    );
};

export const GlobalAppModal = ({
    applicationId,
    categories,
    categorized,
    categoryMap,
    onClose,
    personalFinance,
    personalId,
    personalShopping,
    rowMap,
    unknownId
}) => {
    const { t } = useTranslation();
    const [currentApplication, setCurrentApplication] = useState(null);
    const [tab, setTab] = useState('main');
    const [buttonClicked, setButtonClicked] = useState(false);
    const [usernameRefsAdded, setUsernameRefsAdded] = useState([]);
    const [usernameRefsAll, setUsernameRefsAll] = useState([]);
    const [usernameRefsModified, setUsernameRefsModified] = useState([]);
    const [usernameRefsRemoved, setUsernameRefsRemoved] = useState([]);

    const params = useMemo(
        () => ({
            global: true,
            id: applicationId,
            includeIdentifierEntity: true,
            includePublisher: true,
            includeUsernameRefs: true,
            // This is the main rest to load the app in the modal. We want to ensure we get the latest identifiers.
            skipIdentifierCache: true
        }),
        [applicationId]
    );

    const { application, loading: loadingApplication } =
        useFetchApplicationByParams(params);

    useEffect(() => {
        if (application && !loadingApplication) {
            setCurrentApplication(application);
        }
    }, [application, loadingApplication]);

    const { postUsernameRefBulk } = usePostUsernameRefBulk(t);
    const { putUsernameRefBulk } = usePutUsernameRefBulk(t);
    const { deleteUsernameRefBulk } = useDeleteUsernameRefBulk(t);

    const { putApplication } = usePutApplication(t);
    const { deleteApplication } = useDeleteApplication(t);

    const { postApplicationMerge } = usePostApplicationMerge(t);

    return (
        <Dialog
            aria-labelledby="updateGlobalAppModal"
            fullScreen
            open
            PaperProps={{ style: { padding: 0 } }}
            TransitionComponent={Transition}
        >
            {loadingApplication ? (
                <Flex justifyContent="center" alignItems="center" height="100%">
                    <CircularProgress />
                </Flex>
            ) : (
                <Application data={currentApplication}>
                    <DialogContent>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <MUITabsWrapper
                                    value={tab}
                                    onChange={(_e, v) => setTab(v)}
                                >
                                    <Tab
                                        value="main"
                                        label="Application"
                                        aria-label="Overview"
                                    />
                                    <Tab
                                        value="tenants"
                                        label="Tenants"
                                        aria-label="Users"
                                    />
                                </MUITabsWrapper>
                            </Grid>
                            {tab === 'main' && (
                                <Grid item xs={12}>
                                    <GlobalApplication
                                        application={currentApplication}
                                        buttonClicked={buttonClicked}
                                        categories={categories}
                                        categorized={categorized}
                                        onClose={onClose}
                                        personalFinance={personalFinance}
                                        personalId={personalId}
                                        personalShopping={personalShopping}
                                        postApplicationMerge={
                                            postApplicationMerge
                                        }
                                        rowMap={rowMap}
                                        setButtonClicked={setButtonClicked}
                                        setCurrentApplication={
                                            setCurrentApplication
                                        }
                                        setUsernameRefsAdded={
                                            setUsernameRefsAdded
                                        }
                                        setUsernameRefsAll={setUsernameRefsAll}
                                        setUsernameRefsModified={
                                            setUsernameRefsModified
                                        }
                                        setUsernameRefsRemoved={
                                            setUsernameRefsRemoved
                                        }
                                        tab={tab}
                                        unknownId={unknownId}
                                    />
                                </Grid>
                            )}
                            {tab === 'tenants' && (
                                <Grid item xs={12}>
                                    <Tenants
                                        application={currentApplication}
                                        tab={tab}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() =>
                                deleteFn({
                                    application: currentApplication,
                                    deleteApplication,
                                    onClose,
                                    rowMap
                                })
                            }
                            color="primary"
                            variant="outlined"
                        >
                            {t('Common.delete')}
                        </Button>
                        <Button
                            onClick={() => handleClose({ onClose, rowMap })}
                            color="primary"
                            variant="outlined"
                        >
                            {t('GlobalAdmin.Applications.reset_pull_details')}
                        </Button>

                        <Button
                            onClick={() => handleClose({ onClose, rowMap })}
                            color="primary"
                            variant="outlined"
                        >
                            {t('Common.close')}
                        </Button>
                        <Button
                            onClick={() =>
                                save({
                                    application: currentApplication,
                                    categoryMap,
                                    deleteUsernameRefBulk,
                                    onClose,
                                    postUsernameRefBulk,
                                    putApplication,
                                    putUsernameRefBulk,
                                    rowMap,
                                    setCurrentApplication,
                                    usernameRefsAdded,
                                    usernameRefsAll,
                                    usernameRefsModified,
                                    usernameRefsRemoved
                                })
                            }
                            color="primary"
                            variant="contained"
                        >
                            {t('Common.save')}
                        </Button>
                    </DialogActions>
                </Application>
            )}
        </Dialog>
    );
};
